import {
  authProvidersResponseSchema,
  meResponseSchema,
  recoveryCodesResponseSchema,
  totpEnrollmentResponseSchema,
} from '@apovisio/contracts';
import type { MeResponse } from '@apovisio/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiClientError, apiFetch } from '../../lib/api';

export const ME_QUERY_KEY = ['auth', 'me'] as const;

/** Session admin courante ; `null` si aucune session (401). */
export function useMe() {
  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: async (): Promise<MeResponse | null> => {
      try {
        return await apiFetch('/api/auth/session', { schema: meResponseSchema });
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 30_000,
  });
}

export function useAuthProviders() {
  return useQuery({
    queryKey: ['auth', 'providers'],
    queryFn: () => apiFetch('/api/auth/providers', { schema: authProvidersResponseSchema }),
    staleTime: Infinity,
  });
}

function useRefreshMe() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
}

export function useLogin() {
  const refresh = useRefreshMe();
  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      apiFetch<undefined>('/api/auth/login', { method: 'POST', body: credentials }),
    onSuccess: refresh,
  });
}

export function useDevLogin() {
  const refresh = useRefreshMe();
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<undefined>('/api/auth/dev-login', { method: 'POST', body: { email } }),
    onSuccess: refresh,
  });
}

export function useStartEnrollment() {
  return useMutation({
    mutationFn: () =>
      apiFetch('/api/auth/totp/enrollment', {
        method: 'POST',
        schema: totpEnrollmentResponseSchema,
      }),
  });
}

export function useConfirmEnrollment() {
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch('/api/auth/totp/enrollment/confirm', {
        method: 'POST',
        body: { code },
        schema: recoveryCodesResponseSchema,
      }),
  });
}

export function useVerifyTotp() {
  const refresh = useRefreshMe();
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<undefined>('/api/auth/totp/verify', { method: 'POST', body: { code } }),
    onSuccess: refresh,
  });
}

export function useRecoveryCode() {
  const refresh = useRefreshMe();
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<undefined>('/api/auth/totp/recovery', { method: 'POST', body: { code } }),
    onSuccess: refresh,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<undefined>('/api/auth/logout', { method: 'POST' }),
    onSettled: () => {
      queryClient.clear();
    },
  });
}
